<template>
  <div class="internal-chat-message" :class="{ 'is-mine': isMine }">
    <woot-thumbnail
      v-if="!isMine"
      :src="sender.avatar_url"
      :username="sender.name"
      size="28px"
      class="internal-chat-message__avatar"
      :class="{ 'internal-chat-message__avatar--hidden': !showAvatar }"
    />
    <div class="internal-chat-message__bubble">
      <div
        v-if="message.files && message.files.length"
        class="internal-chat-message__files"
      >
        <div
          v-for="file in message.files"
          :key="file.id"
          class="internal-chat-message__file"
        >
          <a
            v-if="isImage(file)"
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              :src="file.url"
              :alt="file.filename"
              class="internal-chat-message__image"
            />
          </a>
          <audio
            v-else-if="isAudio(file)"
            :src="file.url"
            controls
            class="internal-chat-message__audio"
          />
          <a
            v-else
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
            class="internal-chat-message__attachment"
          >
            <fluent-icon icon="document" size="16" />
            <span class="internal-chat-message__attachment-name text-truncate">
              {{ file.filename }}
            </span>
            <span class="internal-chat-message__attachment-size">
              {{ formatBytes(file.byte_size, 0) }}
            </span>
          </a>
        </div>
      </div>

      <p v-if="message.content" class="internal-chat-message__content">
        {{ message.content }}
      </p>

      <div class="internal-chat-message__meta">
        <span class="internal-chat-message__time">{{ formattedDateTime }}</span>
        <fluent-icon
          v-if="isMine"
          :icon="statusIcon"
          size="14"
          class="internal-chat-message__status-icon"
          :class="`is-${message.status}`"
          :title="statusLabel"
        />
      </div>
    </div>
    <woot-thumbnail
      v-if="isMine"
      :src="sender.avatar_url"
      :username="sender.name"
      size="28px"
      class="internal-chat-message__avatar"
      :class="{ 'internal-chat-message__avatar--hidden': !showAvatar }"
    />
  </div>
</template>

<script>
import { format } from 'date-fns';
import { formatBytes } from 'shared/helpers/FileHelper';

export default {
  props: {
    message: {
      type: Object,
      required: true,
    },
    isMine: {
      type: Boolean,
      default: false,
    },
    sender: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    statusIcon() {
      return this.message.status === 'read' ? 'checkmark-circle' : 'checkmark';
    },
    statusLabel() {
      return this.$t(
        `INTERNAL_CHAT.MESSAGE_STATUS.${this.message.status.toUpperCase()}`
      );
    },
    formattedDateTime() {
      return format(new Date(this.message.created_at), 'dd/MM/yyyy HH:mm');
    },
  },
  methods: {
    formatBytes,
    isImage(file) {
      return (file.content_type || '').startsWith('image');
    },
    isAudio(file) {
      return (file.content_type || '').startsWith('audio');
    },
  },
};
</script>

<style scoped lang="scss">
.internal-chat-message {
  display: flex;
  align-items: flex-end;
  gap: var(--space-smaller);
  margin-bottom: var(--space-small);

  &.is-mine {
    justify-content: flex-end;
  }
}

.internal-chat-message__avatar {
  flex-shrink: 0;

  &--hidden {
    visibility: hidden;
  }
}

.internal-chat-message__bubble {
  max-width: 60%;
  padding: var(--space-smaller) var(--space-small);
  border-radius: var(--border-radius-normal);
  background: var(--color-background-light);

  .is-mine & {
    background: var(--w-100);
  }
}

.internal-chat-message__content {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.internal-chat-message__files {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  margin-bottom: var(--space-smaller);
}

.internal-chat-message__image {
  max-width: 16rem;
  max-height: 16rem;
  border-radius: var(--border-radius-normal);
  display: block;
}

.internal-chat-message__audio {
  max-width: 16rem;
}

.internal-chat-message__attachment {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  padding: var(--space-smaller);
  background: var(--white);
  border-radius: var(--border-radius-normal);
  color: var(--b-700);
  max-width: 16rem;
}

.internal-chat-message__attachment-name {
  flex: 1;
  min-width: 0;
}

.internal-chat-message__attachment-size {
  font-size: var(--font-size-mini);
  color: var(--s-500);
  white-space: nowrap;
}

.internal-chat-message__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-micro);
  margin-top: var(--space-micro);
}

.internal-chat-message__time {
  font-size: var(--font-size-mini);
  color: var(--s-500);
}

.internal-chat-message__status-icon {
  color: var(--s-500);

  &.is-read {
    color: var(--w-500);
  }
}
</style>
